import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import * as React from "react";
import { z } from "zod";
import { ComposeNoAccountsCard } from "@/components/dashboard/compose/ComposeNoAccountsCard";
import {
	type ComposeInitialValues,
	ComposeWorkspace,
} from "@/components/dashboard/compose/ComposeWorkspace";
import type { ComposeMediaItem } from "@/components/dashboard/compose/types";
import { useDashboardContext } from "@/components/dashboard/DashboardContext";
import {
	DashboardContent,
	DashboardLayout,
} from "@/components/dashboard/DashboardLayout";
import type { UnifiedPostTarget } from "@/components/dashboard/posts/types";
import {
	apiRequest,
	useConnectedUnifiedAccounts,
} from "@/components/dashboard/posts/use-unified-post-crud";
import {
	COMPOSE_PROVIDER_ORDER,
	PROVIDER_SUPPORTS,
} from "@/lib/social-provider-catalog";

const composeSearchSchema = z.object({
	assetId: z.string().optional(),
	editPostId: z.string().optional(),
	scheduledAt: z.string().datetime().optional(),
	type: z.enum(["text", "image", "video"]).optional(),
});

export const Route = createFileRoute("/dashboard/compose")({
	component: ComposePage,
	validateSearch: composeSearchSchema,
});

type PostType = "text" | "image" | "video";

type EditablePostPayload = {
	canonicalContent?: {
		brief?: string | null;
		sharedText?: string | null;
	};
	canonicalMedia?: {
		items?: Array<{
			assetId?: string | null;
			assetUrl?: string | null;
			coverImageAssetId?: string | null;
			coverImageUrl?: string | null;
			mediaType?: "image" | "video" | null;
		}>;
	};
	contentBrief: string;
	deliveries?: Array<{
		provider: UnifiedPostTarget;
		socialChannelId?: string | null;
	}>;
	generatedAssetId?: string | null;
	generatedAssetUrl?: string | null;
	generatedMediaItems?: Array<{
		assetId?: string | null;
		assetUrl?: string | null;
		coverImageAssetId?: string | null;
		coverImageUrl?: string | null;
		mediaType?: "image" | "video" | null;
	}>;
	id: string;
	platformOverrides?: Record<string, unknown>;
	platforms?: UnifiedPostTarget[];
	postType: string;
	scheduledAt?: string | null;
};

const POST_TYPE_PLATFORM_ORDER: UnifiedPostTarget[] = [
	...COMPOSE_PROVIDER_ORDER,
];

function providersSupporting(type: PostType): UnifiedPostTarget[] {
	return POST_TYPE_PLATFORM_ORDER.filter((provider) =>
		PROVIDER_SUPPORTS[provider].includes(type),
	);
}

type MediaAssetRaw = {
	assetId: string;
	mediaType: "image" | "video";
	sourceUrl: string;
	originalFilename: string | null;
};

function ComposePage() {
	const {
		assetId: prefilledAssetId,
		editPostId,
		scheduledAt: prefilledScheduledAt,
		type: requestedPostType,
	} = Route.useSearch();
	const navigate = useNavigate();
	const { workspace } = useDashboardContext();
	const accountsQuery = useConnectedUnifiedAccounts(workspace?.id);
	const connectedAccounts = accountsQuery.data ?? [];

	const setRequestedPostType = React.useCallback(
		(next: PostType | null, options?: { replace?: boolean }) => {
			navigate({
				to: "/dashboard/compose",
				search: (prev) => ({ ...prev, type: next ?? undefined }),
				replace: options?.replace,
			});
		},
		[navigate],
	);

	const [initialMedia, setInitialMedia] = React.useState<ComposeMediaItem[]>(
		[],
	);

	const editPostQuery = useQuery({
		enabled: Boolean(editPostId),
		queryKey: ["compose-edit-post", editPostId],
		queryFn: async () => {
			const payload = await apiRequest<{ post: EditablePostPayload }>(
				`/api/social/posts/${encodeURIComponent(editPostId ?? "")}`,
			);
			return payload.post;
		},
	});

	const prefillQuery = useQuery({
		enabled: Boolean(prefilledAssetId),
		queryKey: ["compose-prefill-asset", prefilledAssetId],
		queryFn: async () => {
			const payload = await apiRequest<{ assets: MediaAssetRaw[] }>(
				"/api/social/media",
			);
			return (
				payload.assets?.find((asset) => asset.assetId === prefilledAssetId) ??
				null
			);
		},
	});

	const seededAssetId = React.useRef<string | null>(null);
	React.useEffect(() => {
		const asset = prefillQuery.data;
		if (!asset) return;
		if (seededAssetId.current === asset.assetId) return;
		seededAssetId.current = asset.assetId;
		setRequestedPostType(asset.mediaType, { replace: true });
		setInitialMedia((current) => {
			if (current.some((item) => item.assetId === asset.assetId)) {
				return current;
			}
			return [
				...current,
				{
					assetId: asset.assetId,
					filename: asset.originalFilename ?? asset.assetId,
					mediaType: asset.mediaType,
					sourceUrl: asset.sourceUrl,
				},
			];
		});
	}, [prefillQuery.data, setRequestedPostType]);

	React.useEffect(() => {
		const editPost = editPostQuery.data;
		if (!editPost) return;
		if (
			editPost.postType === "text" ||
			editPost.postType === "image" ||
			editPost.postType === "video"
		) {
			setRequestedPostType(editPost.postType, { replace: true });
		}
	}, [editPostQuery.data, setRequestedPostType]);

	const editInitialValues = React.useMemo<
		ComposeInitialValues | undefined
	>(() => {
		const editPost = editPostQuery.data;
		if (!editPost) return undefined;
		const rawMediaItems = editPost.generatedMediaItems?.length
			? editPost.generatedMediaItems
			: editPost.canonicalMedia?.items?.length
				? editPost.canonicalMedia.items
				: editPost.generatedAssetUrl || editPost.generatedAssetId
					? [
							{
								assetId: editPost.generatedAssetId,
								assetUrl: editPost.generatedAssetUrl,
								coverImageAssetId: null,
								coverImageUrl: null,
								mediaType:
									editPost.postType === "image" || editPost.postType === "video"
										? editPost.postType
										: null,
							},
						]
					: [];
		const mediaItems = rawMediaItems.flatMap((item, index) => {
			if (!item.assetId && !item.assetUrl) return [];
			const mediaType: "image" | "video" | null =
				item.mediaType === "image" || item.mediaType === "video"
					? item.mediaType
					: editPost.postType === "image" || editPost.postType === "video"
						? editPost.postType
						: null;
			if (!mediaType) return [];
			return [
				{
					assetId: item.assetId ?? item.assetUrl ?? `${editPost.id}-${index}`,
					coverImageAssetId: item.coverImageAssetId ?? undefined,
					coverImageUrl: item.coverImageUrl ?? undefined,
					filename: item.assetId ?? `Media ${index + 1}`,
					mediaType,
					sourceUrl: item.assetUrl ?? "",
				},
			];
		});
		const channelIds = new Set(
			(editPost.deliveries ?? []).flatMap((delivery) =>
				delivery.socialChannelId ? [delivery.socialChannelId] : [],
			),
		);
		const providers = new Set(editPost.platforms ?? []);
		const selectedAccountIds =
			channelIds.size > 0
				? connectedAccounts.flatMap((account) =>
						channelIds.has(account.id) ? [account.id] : [],
					)
				: Array.from(providers).flatMap((provider) => {
						const account = connectedAccounts.find(
							(candidate) => candidate.provider === provider,
						);
						return account ? [account.id] : [];
					});
		return {
			caption:
				editPost.canonicalContent?.sharedText ??
				editPost.canonicalContent?.brief ??
				editPost.contentBrief,
			mediaItems,
			overrides:
				editPost.platformOverrides as ComposeInitialValues["overrides"],
			scheduledAt: editPost.scheduledAt ?? null,
			selectedAccountIds,
		};
	}, [connectedAccounts, editPostQuery.data]);

	const prefilledInitialValues = React.useMemo<
		ComposeInitialValues | undefined
	>(() => {
		if (editPostId) return undefined;
		if (!prefilledScheduledAt) return undefined;
		return { scheduledAt: prefilledScheduledAt };
	}, [editPostId, prefilledScheduledAt]);

	const initialPostType =
		editPostQuery.data?.postType === "text" ||
		editPostQuery.data?.postType === "image" ||
		editPostQuery.data?.postType === "video"
			? editPostQuery.data.postType
			: requestedPostType;

	if (editPostId && editPostQuery.isPending) {
		return (
			<DashboardLayout>
				<DashboardContent>
					<p className="text-sm text-muted-foreground">Loading post…</p>
				</DashboardContent>
			</DashboardLayout>
		);
	}

	if (!workspace?.id) {
		return (
			<DashboardLayout>
				<DashboardContent>
					<p className="text-sm text-muted-foreground">Loading workspace…</p>
				</DashboardContent>
			</DashboardLayout>
		);
	}

	if (accountsQuery.isPending) {
		return (
			<DashboardLayout>
				<DashboardContent>
					<p className="text-sm text-muted-foreground">Loading accounts…</p>
				</DashboardContent>
			</DashboardLayout>
		);
	}

	if (connectedAccounts.length === 0) {
		return (
			<DashboardLayout>
				<DashboardContent>
					<ComposeNoAccountsCard
						postType={initialPostType}
						supportedProviders={
							initialPostType
								? providersSupporting(initialPostType)
								: POST_TYPE_PLATFORM_ORDER
						}
					/>
				</DashboardContent>
			</DashboardLayout>
		);
	}

	return (
		<DashboardLayout className="flex flex-col pb-0 lg:overflow-hidden">
			<ComposeWorkspace
				accounts={connectedAccounts}
				editPostId={editPostId}
				initialPostType={initialPostType}
				initialValues={editInitialValues ?? prefilledInitialValues}
				initialMedia={initialMedia}
				workspaceId={workspace.id}
			/>
		</DashboardLayout>
	);
}
